/**
 * Lifting an event list to keys (W1-04 core-ir; DESIGN.md §6.5, the exact inverse of lowerTrack.ts).
 *
 *   for each event e (sorted, non-overlapping, positive length after normalisation):
 *     E = e.bezier != 0 ? Bezier : e.start == e.end ? Hold : Tween(id, slice)      (eventFields.ts, raw kept in src)
 *     no keys yet                 -> key(e.startTime, vIn = vOut = e.start, E)
 *     last key at e.startTime     -> last.vOut = e.start, last.ease = E           (abutting: a jump if different)
 *     otherwise (a gap)           -> last.ease = Hold{gap}; key(e.startTime, vIn = last.vOut, vOut = e.start, E)
 *     provisional end key(e.endTime, vIn = vOut = e.end, Hold)                   (overwritten by an abutting event)
 *   raw non-normalised triples -> src.rawStart / src.rawEnd; linkgroup -> key.link; unknown keys -> src.extra.
 *
 * Special cases:
 *  - speed: the speed mode's integrator decides (eventFields.ts); an rpe170 step (easingType 0) holds `start` and
 *    its end key leaves at the raw `end` (Phira's 170 integrator continues with the raw end speed);
 *  - events Lower produced itself are recognised by their origin so lift(lower(doc)) gives the document back:
 *    'padding' is dropped (padding is re-applied on export), 'single' is a lone key, 'trailing' is the jump of the
 *    last key; imported events are always 'segment', and a last line event with exactly the 'single' / 'trailing'
 *    shape is lifted the same way (LiftCollapse), so a chart read back without its sidecar keeps its key count;
 *  - a zero-length event (never in normalised IR) is a jump to its end value; an event that starts before the
 *    previous one ended, or has an invalid triple, is skipped and reported by the caller.
 * Implicit-default ghost keys (Phira's prepended colour / text / incline / paint default) are NOT materialised.
 */

import { HOLD } from '../ease/index';
import type { Ease } from '../ease/types';
import { trackValueEquals } from '../model/invariants';
import type { KeySrc, LineProp, SpeedMode, TrackValue } from '../model/types';
import { Beats } from '../time/beat';
import type { Beat, Triple } from '../time/types';
import type { Passthrough } from '../util/types';
import { liftEventEase, liftExtraEase, type LiftedEase, type MutableKeySrc } from './eventFields';
import { beatPlus, tripleIn } from './triples';
import type { IrEvent, IrEventOrigin, IrExtraKeyframe, LiftedKey } from './types';

/** One event to lift, independent of its source shape (RPE event or extra.json keyframe). */
export interface LiftItem {
  readonly startTime: Triple;
  readonly endTime: Triple;
  readonly start: TrackValue;
  readonly end: TrackValue;
  readonly origin: IrEventOrigin;
  /** Read from a file (no key produced it: `IrEvent.srcKey` null): its origin says nothing about Lower's shapes. */
  readonly fromFile: boolean;
  readonly link: number;
  readonly extra: Passthrough;
  /** Model ease of the event, writing provenance into `src`. */
  ease(constant: boolean, src: MutableKeySrc): LiftedEase;
}

interface KeyDraft {
  readonly t: Beat;
  vIn: TrackValue;
  vOut: TrackValue;
  ease: Ease;
  link: number;
  readonly src: MutableKeySrc;
}

/** Lifted key data (`LiftedKey` widened to the extra.json value types). */
export interface LiftKeyData {
  readonly t: Beat;
  readonly vIn: TrackValue;
  readonly vOut: TrackValue;
  readonly ease: Ease;
  readonly link: number;
  readonly src?: KeySrc;
}

export interface LiftTrackResult {
  readonly keys: LiftKeyData[];
  /** Indices of events that could not be lifted (invalid triples, negative length, unsorted / overlapping). */
  readonly skipped: number[];
}

const GAP: Ease = { kind: 'hold', gap: true };

function startSrc(raw: Triple | undefined, extra: Passthrough): MutableKeySrc {
  const src: MutableKeySrc = {};
  if (raw !== undefined) src.rawStart = raw;
  if (extra.length > 0) src.extra = extra;
  return src;
}

function finish(d: KeyDraft): LiftKeyData {
  const base = { t: d.t, vIn: d.vIn, vOut: d.vOut, ease: d.ease, link: d.link };
  return Object.keys(d.src).length > 0 ? { ...base, src: d.src } : base;
}

/**
 * Recognition of Lower's own lone-key and last-key-jump events when their origin is unknown (a chart read back
 * from a file without the sidecar, UX-026; events Lower produced in memory carry their origin and are never
 * re-interpreted): the LAST event read from a file that is constant and spans exactly
 * `beats` = 1 / `den` beat from its start, with a normalised end triple, is lifted as the lone key ('single', when
 * it is the only event) or as the jump of the last key ('trailing'; only when `trailing` and its value differs from
 * the value held before it), exactly the shapes Lower writes for those keys. Lowering the lifted keys then writes
 * the identical event again, so export -> import -> export is a fixed point for events AND keys; any other last
 * event keeps its own end key (dropping it would change the IR, e.g. max_time).
 */
export interface LiftCollapse {
  /** Span of Lower's lone-key event: 1 / den beat (1 for line tracks, 32 for GIF progress). */
  readonly den: number;
  /** Lower writes a trailing jump event on this track kind (never on GIF progress). */
  readonly trailing: boolean;
}

/** Whether item e (the last of its list) has the shape of Lower's lone-key or trailing event. */
function lowerShaped(
  e: LiftItem,
  s: { readonly beat: Beat },
  en: { readonly beat: Beat; readonly raw: Triple | undefined },
  last: KeyDraft | undefined,
  collapse: LiftCollapse,
): boolean {
  if (!e.fromFile || e.origin !== 'segment' || en.raw !== undefined || !trackValueEquals(e.start, e.end)) return false;
  if (!Beats.eq(en.beat, beatPlus(s.beat, 1, collapse.den))) return false;
  if (last === undefined) return true;
  return collapse.trailing && collapse.den === 1 && !trackValueEquals(last.vOut, e.start);
}

/** The key walk of DESIGN.md §6.5 over generic items (`collapse`: see LiftCollapse; null = never). */
export function liftItems(items: readonly LiftItem[], collapse: LiftCollapse | null = null): LiftTrackResult {
  const keys: KeyDraft[] = [];
  const skipped: number[] = [];
  let lastItem = items.length - 1;
  while (lastItem >= 0 && items[lastItem]?.origin === 'padding') lastItem--;
  items.forEach((e, i) => {
    if (e.origin === 'padding') return;
    const s = tripleIn(e.startTime);
    const en = tripleIn(e.endTime);
    const last = keys[keys.length - 1];
    if (s === null || en === null || Beats.lt(en.beat, s.beat) || (last !== undefined && Beats.lt(s.beat, last.t))) {
      skipped.push(i);
      return;
    }
    const src = startSrc(s.raw, e.extra);
    const abuts = last !== undefined && Beats.eq(last.t, s.beat);

    // Constant events Lower wrote for a lone key or a last-key jump (by origin, or by shape when read back).
    if (
      (e.origin === 'single' && last === undefined) ||
      (e.origin === 'trailing' && last !== undefined) ||
      (collapse !== null && i === lastItem && lowerShaped(e, s, en, last, collapse))
    ) {
      e.ease(true, src);
      if (last === undefined) {
        keys.push({ t: s.beat, vIn: e.start, vOut: e.start, ease: HOLD, link: e.link, src });
      } else if (abuts) {
        last.vOut = e.start;
        last.link = e.link;
        Object.assign(last.src, src);
      } else {
        last.ease = GAP;
        keys.push({ t: s.beat, vIn: last.vOut, vOut: e.start, ease: HOLD, link: e.link, src });
      }
      return;
    }

    // Zero-length event: an instant jump to its end value (Phira, when it is not the first event).
    if (Beats.eq(s.beat, en.beat)) {
      if (last === undefined) keys.push({ t: s.beat, vIn: e.end, vOut: e.end, ease: HOLD, link: e.link, src });
      else if (abuts) last.vOut = e.end;
      else {
        last.ease = GAP;
        keys.push({ t: s.beat, vIn: last.vOut, vOut: e.end, ease: HOLD, link: e.link, src });
      }
      return;
    }

    const lifted = e.ease(trackValueEquals(e.start, e.end), src);
    // rpe170 speed step: the raw end value is written back while unedited (src.rawEndValue).
    if (lifted.step && typeof e.end === 'number') src.rawEndValue = e.end;
    if (last === undefined) {
      keys.push({ t: s.beat, vIn: e.start, vOut: e.start, ease: lifted.ease, link: e.link, src });
    } else if (abuts) {
      last.vOut = e.start;
      last.ease = lifted.ease;
      last.link = e.link;
      Object.assign(last.src, src);
    } else {
      last.ease = GAP;
      keys.push({ t: s.beat, vIn: last.vOut, vOut: e.start, ease: lifted.ease, link: e.link, src });
    }
    const endSrc: MutableKeySrc = {};
    if (en.raw !== undefined) endSrc.rawEnd = en.raw;
    keys.push({ t: en.beat, vIn: lifted.step ? e.start : e.end, vOut: e.end, ease: HOLD, link: 0, src: endSrc });
  });
  return { keys: keys.map(finish), skipped };
}

/** Items of an RPE event list (line tracks). `speed`: the speed mode for speed events, else null. */
export function eventItems(events: readonly IrEvent<TrackValue>[], speed: SpeedMode | null): LiftItem[] {
  return events.map((e) => ({
    startTime: e.startTime,
    endTime: e.endTime,
    start: e.start,
    end: e.end,
    origin: e.origin,
    fromFile: e.srcKey === null,
    link: e.linkgroup,
    extra: e.extra,
    ease: (constant, src) =>
      liftEventEase(
        {
          easingType: e.easingType,
          easingLeft: e.easingLeft,
          easingRight: e.easingRight,
          bezier: e.bezier,
          bezierPoints: e.bezierPoints,
        },
        constant,
        speed,
        src,
      ),
  }));
}

/** Items of an extra.json keyframe list (effect / video variables). */
export function extraItems(kfs: readonly IrExtraKeyframe<TrackValue>[]): LiftItem[] {
  return kfs.map((k) => ({
    startTime: k.startTime,
    endTime: k.endTime,
    start: k.start,
    end: k.end,
    origin: k.origin ?? 'segment',
    fromFile: k.srcKey === null,
    link: 0,
    extra: [],
    ease: (constant, src) => ({
      ease: liftExtraEase(k.easingType, k.easingLeft, k.easingRight, constant, src),
      step: false,
    }),
  }));
}

/** `Lift.liftEvents`: keys of one line property list (line values are numbers, RGB colours or strings). */
export function liftedKeys(events: readonly IrEvent<TrackValue>[], prop: LineProp, speedMode: SpeedMode): LiftedKey[] {
  return liftLineEvents(events, prop, speedMode).keys as LiftedKey[];
}

/**
 * Lifts the events of one line property list. `collapse`: recognise Lower's lone-key / trailing shapes on events
 * read from a file (`LiftCollapse`; the chart import does, single lists from the Raw panel and the clipboard keep
 * every event's end key).
 */
export function liftLineEvents(
  events: readonly IrEvent<TrackValue>[],
  prop: LineProp,
  speedMode: SpeedMode,
  collapse = false,
): LiftTrackResult {
  const gif = prop === 'gif';
  const items = eventItems(events, prop === 'speed' ? speedMode : null);
  return liftItems(items, collapse ? { den: gif ? 32 : 1, trailing: !gif } : null);
}
